import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, X, Search } from "lucide-react";
import { toast } from "react-toastify";
import CalendarIcon from "../../../../assets/calender-icon.svg";
import PrintIcon from "../../../../assets/icons/PrintIcon";
import GreenCheck from "../../../../assets/icons/GreenCheck";
import {
  productAPI,
  customersAPI,
  returnVendorAPI,
  imeiAPI,
  getInStockProductsList,
} from "../../../../services/index";
import PrintRepairReserve from "../../repair-reserve/components/PrintRepairReserve";
import "../../../../style/returnsRegister.css";
import { useSelector } from "react-redux";

// Custom hook for click outside
function useClickOutside(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, onClose]);
}

// Format date to YYYY-MM-DD for input
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Format date to DD/MM/YYYY for display (e.g., "01/10/2025")
const formatDateForDisplay = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${year}`;
};

// Convert DD/MM/YYYY format back to YYYY-MM-DD for API
const convertDateToAPIFormat = (dateString) => {
  if (!dateString) return "";

  // Check if already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Parse DD/MM/YYYY format
  const parts = dateString.split("/");
  if (parts.length === 3) {
    const day = parts[0].padStart(2, "0");
    const month = parts[1].padStart(2, "0");
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }

  // Fallback: try to parse as Date and format
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return formatDateForInput(date);
    }
  } catch (e) {
    // If parsing fails, return as is
  }

  return dateString;
};

// Format date input with auto-slash insertion (DD/MM/YYYY)
const formatDateInput = (value) => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, "");

  // Apply formatting based on length
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  } else {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(
      4,
      8
    )}`;
  }
};

function EditVendorReturnModal({ isOpen, onClose, onSubmit, repairId }) {
  const { isPartsURL } = useSelector((state) => state.domain);
  const [imei, setImei] = useState("");
  const [buyingDate, setBuyingDate] = useState("");
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [defectNote, setDefectNote] = useState("");
  const [action, setAction] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingRepair, setLoadingRepair] = useState(false);
  const [repairData, setRepairData] = useState(null);
  const [imeiFound, setImeiFound] = useState(false);
  const [imeiChecking, setImeiChecking] = useState(false);
  const [imeiRecord, setImeiRecord] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [savedRepairData, setSavedRepairData] = useState(null);
  const [deviceId, setDeviceId] = useState(null);

  // Additional fields for "Change Device" action
  const [exchangeModel, setExchangeModel] = useState(null);
  const [vendorName, setVendorName] = useState("");
  const [secondLevelAction, setSecondLevelAction] = useState("");
  const [buyingPrice, setBuyingPrice] = useState("");

  // Dropdown states
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
  const [isExchangeModelDropdownOpen, setIsExchangeModelDropdownOpen] =
    useState(false);
  const [isSecondLevelActionDropdownOpen, setIsSecondLevelActionDropdownOpen] =
    useState(false);
  const [modelSearch, setModelSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [exchangeModelSearch, setExchangeModelSearch] = useState("");

  // Data states
  const [models, setModels] = useState([]);
  const [exchangeModels, setExchangeModels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingExchangeModels, setLoadingExchangeModels] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Refs
  const modelDropdownRef = useRef(null);
  const customerDropdownRef = useRef(null);
  const actionDropdownRef = useRef(null);
  const exchangeModelDropdownRef = useRef(null);
  const secondLevelActionDropdownRef = useRef(null);
  const modelSearchInputRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const exchangeModelSearchInputRef = useRef(null);

  useClickOutside(modelDropdownRef, () => setIsModelDropdownOpen(false));
  useClickOutside(customerDropdownRef, () => setIsCustomerDropdownOpen(false));
  useClickOutside(actionDropdownRef, () => setIsActionDropdownOpen(false));
  useClickOutside(exchangeModelDropdownRef, () =>
    setIsExchangeModelDropdownOpen(false)
  );
  useClickOutside(secondLevelActionDropdownRef, () =>
    setIsSecondLevelActionDropdownOpen(false)
  );

  // Check if IMEI exists in API
  const checkImeiExists = useCallback(async (imeiValue) => {
    if (!imeiValue || imeiValue.trim() === "") {
      setImeiFound(false);
      setImeiRecord(null);
      return;
    }

    setImeiChecking(true);
    try {
      const response = await imeiAPI.getImeiList(imeiValue.trim(), 1, 50);

      // Handle paginated response structure
      // If count > 0 or results array has items, IMEI was found
      const count = response?.count || 0;
      const imeiData =
        response?.results ||
        response?.data ||
        (Array.isArray(response) ? response : []);

      // IMEI is found if:
      // 1. Count > 0 (API found matching records), OR
      // 2. Results array has items (non-paginated response with results)
      const found =
        count > 0 || (Array.isArray(imeiData) && imeiData.length > 0);

      setImeiFound(found);

      // Store the first IMEI record if found
      if (found && Array.isArray(imeiData) && imeiData.length > 0) {
        setImeiRecord(imeiData[0]);
      } else {
        setImeiRecord(null);
      }
    } catch (error) {
      console.error("Error checking IMEI:", error);
      setImeiFound(false);
      setImeiRecord(null);
    } finally {
      setImeiChecking(false);
    }
  }, []);

  // Validate IMEI with debounce
  useEffect(() => {
    if (!imei || imei.trim() === "") {
      setImeiFound(false);
      setImeiRecord(null);
      return;
    }

    // Check IMEI in API with debounce
    const timeoutId = setTimeout(() => {
      checkImeiExists(imei);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [imei, checkImeiExists]);

  // Auto-set action to "Keep for Repair" when IMEI is not found
  useEffect(() => {
    // Only in non-edit mode (when repairId is not set)
    if (repairId) return;

    // If IMEI checking is complete and IMEI was not found
    if (!imeiChecking && imei && imei.trim() !== "" && !imeiFound) {
      // Set action to "keep_for_repair"
      setAction("keep_for_repair");
    }
  }, [imeiFound, imeiChecking, imei, repairId]);

  // IMEI validation state - only valid if found in API (same logic as AddManualReturnsModal)
  // For edit mode: IMEI is valid if it exists (we allow existing IMEIs)
  // For create mode: IMEI must be found in API
  const isImeiValid = imei.trim() !== "" && imeiFound && !imeiChecking;

  // Action options
  const actionOptions = [
    { value: "change_device", label: "Change Device" },
    { value: "keep_for_repair", label: "Keep for Repair" },
    { value: "not_repairable", label: "Not Repairable" },
  ];

  // Second Level Action options (for Change Device)
  const secondLevelActionOptions = [
    { value: "keep_for_vendor", label: "Keep for Supplier" },
    { value: "keep_for_repair", label: "Keep for Repair" },
  ];

  // Fetch models (for Select Model dropdown - uses getProductsList)
  const fetchModels = async (search = "") => {
    setLoadingModels(true);
    try {
      const response = await productAPI.getProductsList({
        search,
        page: 1,
        page_size: 50,
      });

      let productsList = [];
      if (response) {
        if (Array.isArray(response)) {
          productsList = response;
        } else if (response.results && Array.isArray(response.results)) {
          productsList = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          productsList = response.data;
        }
      }

      // Format products for display: Name and Suffix on first line, Ram | Storage | Variants | Status on second line
      const formattedModels = productsList.map((product) => {
        const line1Parts = [];
        if (product.name) line1Parts.push(product.name);
        if (product.suffix) line1Parts.push(product.suffix);
        const line1 = line1Parts.filter((p) => p).join(" ");

        const line2Parts = [];
        if (product.ram) line2Parts.push(product.ram);
        if (product.storage) line2Parts.push(product.storage + "GB");
        if (
          product.variants &&
          Array.isArray(product.variants) &&
          product.variants.length > 0
        ) {
          const variantValues = product.variants
            .map((v) => Object.values(v).join(", "))
            .join(", ");
          if (variantValues) line2Parts.push(variantValues);
        }
        line2Parts.push(product.used_mobile ? "Used" : "New");
        const line2 = line2Parts.filter((p) => p).join(" | ");

        return {
          id: product.id,
          name: line1 + "\n" + line2,
          product: product,
        };
      });

      setModels(formattedModels);
    } catch (error) {
      console.error("Error fetching models:", error);
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  // Fetch exchange models (for Select Exchange Model dropdown - uses getInStockProductsList)
  const fetchExchangeModels = async (search = "") => {
    setLoadingExchangeModels(true);
    try {
      const response = await getInStockProductsList({
        search,
        page: 1,
        page_size: 50,
      });

      let productsList = [];
      if (response) {
        if (Array.isArray(response)) {
          productsList = response;
        } else if (response.results && Array.isArray(response.results)) {
          productsList = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          productsList = response.data;
        }
      }

      // Format products for display: Name and Suffix on first line, Ram | Storage | Variants | Status on second line
      const formattedModels = productsList.map((product) => {
        const line1Parts = [];
        if (product.name) line1Parts.push(product.name);
        if (product.suffix) line1Parts.push(product.suffix);
        const line1 = line1Parts.filter((p) => p).join(" ");

        const line2Parts = [];
        if (product.ram) line2Parts.push(product.ram);
        if (product.storage) line2Parts.push(product.storage + "GB");
        if (
          product.variants &&
          Array.isArray(product.variants) &&
          product.variants.length > 0
        ) {
          const variantValues = product.variants
            .map((v) => Object.values(v).join(", "))
            .join(", ");
          if (variantValues) line2Parts.push(variantValues);
        }
        line2Parts.push(product.used_mobile ? "Used" : "New");
        const line2 = line2Parts.filter((p) => p).join(" | ");

        return {
          id: product.id,
          name: line1 + "\n" + line2,
          product: product,
          unique_id: product.unique_id || null,
        };
      });

      setExchangeModels(formattedModels);
    } catch (error) {
      console.error("Error fetching exchange models:", error);
      setExchangeModels([]);
    } finally {
      setLoadingExchangeModels(false);
    }
  };

  // Fetch customers
  const fetchCustomers = async (search = "") => {
    setLoadingCustomers(true);
    try {
      const result = await customersAPI.getCustomers(search, 1, 50);
      const raw = result?.response ?? result;
      const list = Array.isArray(raw) ? raw : raw?.results ?? raw?.data ?? [];

      const formattedCustomers = (list || [])
        .map((c) => ({
          id: c.id ?? c.customer_id ?? c.pk,
          name: c.name ?? c.customer_name ?? c.user_name ?? "Unknown",
          customer: c,
        }))
        .filter((o) => o.id);

      setCustomers(formattedCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Helper function to format product for display
  const formatProductForDisplay = (product) => {
    if (!product) return null;

    const line1Parts = [];
    if (product.name) line1Parts.push(product.name);
    if (product.suffix) line1Parts.push(product.suffix);
    const line1 = line1Parts.filter((p) => p).join(" ");

    const line2Parts = [];
    if (product.ram) line2Parts.push(product.ram);
    if (product.storage) line2Parts.push(product.storage + "GB");
    if (
      product.variants &&
      Array.isArray(product.variants) &&
      product.variants.length > 0
    ) {
      const variantValues = product.variants
        .map((v) => Object.values(v).join(", "))
        .join(", ");
      if (variantValues) line2Parts.push(variantValues);
    }
    line2Parts.push(product.used_mobile ? "Used" : "New");
    const line2 = line2Parts.filter((p) => p).join(" | ");

    return {
      id: product.id,
      name: line1 + "\n" + line2,
      product: product,
    };
  };

  // Load repair data when editing
  useEffect(() => {
    const loadRepairData = async () => {
      if (!isOpen || !repairId) return;

      setLoadingRepair(true);
      try {
        // Fetch device details - this single API call contains all needed data
        const result = await returnVendorAPI.getDeviceById(repairId);

        if (result.error) {
          toast.error(result.error.message || "Failed to load device data");
          return;
        }

        const repairDataResponse = result.response || {};
        setDeviceId(repairId);
        setRepairData(repairDataResponse);

        // Populate form fields with repair data
        // IMEI field - API returns imei
        const imeiValue = repairDataResponse.imei;
        if (imeiValue) {
          setImei(imeiValue);
          // In edit mode, set imeiFound to true (existing IMEIs are valid)
          // No need to call checkImeiExists - we already have the data from API
          setImeiFound(true);
          // Set IMEI record from API response for supplier info
          if (repairDataResponse.vendor || repairDataResponse.vendor_name) {
            setImeiRecord({
              supplier: repairDataResponse.vendor,
              supplier_name: repairDataResponse.vendor_name,
            });
          }
        }

        // Buying Date - use buying_date if available, otherwise use updated_at or today
        if (repairDataResponse.buying_date) {
          setBuyingDate(
            formatDateForDisplay(new Date(repairDataResponse.buying_date))
          );
        } else if (repairDataResponse.updated_at) {
          setBuyingDate(
            formatDateForDisplay(new Date(repairDataResponse.updated_at))
          );
        } else {
          setBuyingDate(formatDateForDisplay(new Date()));
        }

        // Defect Note - API returns note field
        setDefectNote(repairDataResponse.note || "");

        // Set model directly from API response
        if (repairDataResponse.product) {
          const product = repairDataResponse.product;
          const formattedModel = formatProductForDisplay(product);
          if (formattedModel) {
            setSelectedModel(formattedModel);
          }
        }

        // Set customer directly from API response
        if (repairDataResponse.customer && repairDataResponse.customer_name) {
          const customerId =
            typeof repairDataResponse.customer === "string"
              ? parseInt(repairDataResponse.customer, 10)
              : repairDataResponse.customer;
          const customerName = repairDataResponse.customer_name;

          setSelectedCustomer({
            id: customerId,
            name: customerName,
            customer: {
              id: customerId,
              name: customerName,
            },
          });
        }

        // Set action - API returns action field directly
        if (repairDataResponse.action) {
          const actionValue = repairDataResponse.action.toLowerCase();
          if (actionValue === "change device") {
            setAction("change_device");
          } else if (actionValue === "keep for repair") {
            setAction("keep_for_repair");
          } else if (actionValue === "not repairable") {
            setAction("not_repairable");
          }
        }

        // Set change device specific fields
        if (repairDataResponse.second_level_action) {
          const secondLevelValue =
            repairDataResponse.second_level_action.toLowerCase();
          if (
            secondLevelValue === "keep for vendor" ||
            secondLevelValue === "keep for supplier"
          ) {
            setSecondLevelAction("keep_for_vendor");
          } else if (secondLevelValue === "keep for repair") {
            setSecondLevelAction("keep_for_repair");
          }
        }

        // Set buying price - API returns buying_price
        if (repairDataResponse.buying_price) {
          setBuyingPrice(repairDataResponse.buying_price.toString());
        } else if (repairDataResponse.product?.latest_buying_price) {
          setBuyingPrice(
            repairDataResponse.product.latest_buying_price.toString()
          );
        }

        // Supplier name - API returns vendor_name
        if (repairDataResponse.vendor_name) {
          setVendorName(repairDataResponse.vendor_name);
        }

        // Set exchange model directly from API response
        if (repairDataResponse.replacement_product) {
          const replacementProduct = repairDataResponse.replacement_product;

          if (typeof replacementProduct === "object" && replacementProduct.id) {
            // replacement_product is a full object
            const formattedModel = formatProductForDisplay(replacementProduct);
            if (formattedModel) {
              setExchangeModel(formattedModel);
            }
          }
          // If replacement_product is just an ID, leave exchange model empty
          // User will need to select from dropdown
        }
      } catch (error) {
        console.error("Error loading repair data:", error);
        toast.error("Failed to load repair data");
      } finally {
        setLoadingRepair(false);
      }
    };

    if (isOpen && repairId) {
      loadRepairData();
    }
  }, [isOpen, repairId]);

  // Note: Models and customers are loaded lazily when dropdowns are opened
  // This avoids unnecessary API calls when the modal opens

  // Fetch models when dropdown opens (lazy loading)
  useEffect(() => {
    if (isModelDropdownOpen && models.length === 0) {
      fetchModels(modelSearch);
    }
  }, [isModelDropdownOpen]);

  // Search models
  useEffect(() => {
    if (isModelDropdownOpen) {
      const timeoutId = setTimeout(() => {
        fetchModels(modelSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [modelSearch, isModelDropdownOpen]);

  // Auto-focus model search input when dropdown opens
  useEffect(() => {
    if (isModelDropdownOpen && modelSearchInputRef.current) {
      setTimeout(() => {
        modelSearchInputRef.current.focus();
      }, 0);
    }
  }, [isModelDropdownOpen]);

  // Fetch customers when dropdown opens (lazy loading)
  useEffect(() => {
    if (isCustomerDropdownOpen && customers.length === 0) {
      fetchCustomers(customerSearch);
    }
  }, [isCustomerDropdownOpen]);

  // Search customers
  useEffect(() => {
    if (isCustomerDropdownOpen) {
      const timeoutId = setTimeout(() => {
        fetchCustomers(customerSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [customerSearch, isCustomerDropdownOpen]);

  // Auto-focus customer search input when dropdown opens
  useEffect(() => {
    if (isCustomerDropdownOpen && customerSearchInputRef.current) {
      setTimeout(() => {
        customerSearchInputRef.current.focus();
      }, 0);
    }
  }, [isCustomerDropdownOpen]);

  // Fetch exchange models when dropdown opens (lazy loading)
  useEffect(() => {
    if (isExchangeModelDropdownOpen && exchangeModels.length === 0) {
      fetchExchangeModels(exchangeModelSearch);
    }
  }, [isExchangeModelDropdownOpen]);

  // Search exchange models
  useEffect(() => {
    if (isExchangeModelDropdownOpen) {
      const timeoutId = setTimeout(() => {
        fetchExchangeModels(exchangeModelSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [exchangeModelSearch, isExchangeModelDropdownOpen]);

  // Auto-focus exchange model search input when dropdown opens
  useEffect(() => {
    if (isExchangeModelDropdownOpen && exchangeModelSearchInputRef.current) {
      setTimeout(() => {
        exchangeModelSearchInputRef.current.focus();
      }, 0);
    }
  }, [isExchangeModelDropdownOpen]);

  // Auto-set buying date when IMEI becomes valid
  // Clear buying date when IMEI is not found or cleared
  useEffect(() => {
    // Only auto-set/clear in non-edit mode (when repairId is not set)
    if (repairId) return;

    // If IMEI is empty, clear buying date
    if (!imei || imei.trim() === "") {
      setBuyingDate("");
      return;
    }

    // If IMEI check is still in progress, don't change buying date
    if (imeiChecking) return;

    // If IMEI is valid and found, set buying date
    if (isImeiValid && imeiRecord?.created_at) {
      // Use created_at from IMEI record, format to DD/MM/YYYY
      const createdDate = formatDateForDisplay(new Date(imeiRecord.created_at));
      setBuyingDate(createdDate);
    } else if (isImeiValid && !imeiRecord?.created_at) {
      // Fallback to today if created_at is not available, format to DD/MM/YYYY
      const today = formatDateForDisplay(new Date());
      setBuyingDate(today);
    } else if (!imeiFound) {
      // Clear buying date when IMEI is not found (after check completes)
      setBuyingDate("");
    }
  }, [isImeiValid, imeiRecord, imei, imeiFound, imeiChecking, repairId]);

  // Auto-populate selling price from selected model when second level action is keep_for_vendor
  useEffect(() => {
    if (
      selectedModel &&
      selectedModel.product &&
      secondLevelAction === "keep_for_vendor"
    ) {
      const latestSellingPrice =
        selectedModel.product.latest_selling_price ||
        selectedModel.product.avg_selling_price ||
        "";

      if (
        latestSellingPrice !== "" &&
        latestSellingPrice !== null &&
        latestSellingPrice !== undefined
      ) {
        setBuyingPrice(String(latestSellingPrice));
      }
    }
  }, [selectedModel, secondLevelAction]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setImei("");
      setBuyingDate("");
      setSelectedModel(null);
      setSelectedCustomer(null);
      setDefectNote("");
      setAction("");
      setExchangeModel(null);
      setVendorName("");
      setSecondLevelAction("");
      setBuyingPrice("");
      setModelSearch("");
      setCustomerSearch("");
      setExchangeModelSearch("");
      setIsModelDropdownOpen(false);
      setIsCustomerDropdownOpen(false);
      setIsActionDropdownOpen(false);
      setIsExchangeModelDropdownOpen(false);
      setIsSecondLevelActionDropdownOpen(false);
      setIsSubmitting(false);
      setRepairData(null);
      setDeviceId(null);
      setModels([]);
      setExchangeModels([]);
      setShowPrintModal(false);
      setSavedRepairData(null);
    }
  }, [isOpen]);

  // Filter models based on search
  const filteredModels = models.filter((model) =>
    model.name.toLowerCase().includes(modelSearch.toLowerCase())
  );

  // Use exchange models directly from API response (already filtered by search on server-side)
  const filteredExchangeModels = exchangeModels;

  // Filter customers based on search
  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Auto-select exchange model when unique_id search returns exactly one result
  useEffect(() => {
    if (
      exchangeModelSearch &&
      exchangeModelSearch.trim() !== "" &&
      filteredExchangeModels.length === 1
    ) {
      const foundModel = filteredExchangeModels[0];
      // Check if search term matches unique_id exactly
      if (
        foundModel.unique_id &&
        foundModel.unique_id.toLowerCase() === exchangeModelSearch.toLowerCase()
      ) {
        setExchangeModel(foundModel);
        setIsExchangeModelDropdownOpen(false);
        setExchangeModelSearch("");
      }
    }
  }, [filteredExchangeModels, exchangeModelSearch]);

  // Check if "Change Device" is selected
  const isChangeDevice = action === "change_device";

  const handleSubmit = async () => {
    // Validation
    if (!imei || imei.trim() === "") {
      toast.error("IMEI is required");
      return;
    }

    if (!selectedCustomer) {
      toast.error("Customer is required");
      return;
    }

    // If IMEI not found, defect note is mandatory
    if (!imeiFound && (!defectNote || defectNote.trim() === "")) {
      toast.error("Defect note is required when IMEI is not found");
      return;
    }

    if (!action) {
      toast.error("Action is required");
      return;
    }

    // Additional validation for "Change Device" action
    if (isChangeDevice) {
      if (!exchangeModel) {
        toast.error("Exchange Model is required");
        return;
      }

      if (!vendorName || vendorName.trim() === "") {
        toast.error("Supplier information is required.");
        return;
      }

      if (!secondLevelAction) {
        toast.error("2nd Level Action is required");
        return;
      }

      if (
        secondLevelAction === "keep_for_vendor" &&
        (!buyingPrice || buyingPrice.trim() === "")
      ) {
        toast.error(
          "Buying Price is required when Keep for Supplier is selected"
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Map action values to API format
      const actionMap = {
        change_device: "Change Device",
        keep_for_repair: "Keep for Repair",
        not_repairable: "Not Repairable",
      };

      // Map second level action values to API format
      const secondLevelActionMap = {
        keep_for_vendor: "Keep for Vendor",
        keep_for_repair: "Keep for Repair",
      };

      // Prepare form data based on API schema
      const formData = {
        imei: imei.trim(),
        customer: selectedCustomer?.id || 0,
        action: actionMap[action] || action,
      };

      // Only include note if it's provided
      if (defectNote && defectNote.trim() !== "") {
        formData.note = defectNote.trim();
      }

      // Only include buying_date if it's provided
      if (buyingDate && buyingDate.trim() !== "") {
        formData.buying_date = convertDateToAPIFormat(buyingDate);
      }

      // Only include product if it's selected
      if (selectedModel?.product?.id || selectedModel?.id) {
        formData.product = selectedModel?.product?.id || selectedModel?.id;
      }

      // Additional fields for Change Device
      if (isChangeDevice) {
        formData.vendor = imeiRecord?.supplier || 0;
        formData.second_level_action =
          secondLevelActionMap[secondLevelAction] || secondLevelAction;
        formData.replacement_product =
          exchangeModel?.product?.id || exchangeModel?.id || 0;

        if (secondLevelAction === "keep_for_vendor" && buyingPrice) {
          formData.buying_price = buyingPrice.trim();
        }
      }

      if (onSubmit) {
        await onSubmit(formData);
        // Close modal after successful submission
        onClose();
      } else {
        // Default success message
        toast.success("Transaction updated successfully");
        onClose();
      }
    } catch (error) {
      console.error("Error submitting return:", error);
      toast.error("Failed to register return");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Create Label - Save repair and show print modal
  const handleCreateLabel = async () => {
    // Validation (same as handleSubmit)
    if (!imei || imei.trim() === "") {
      toast.error("IMEI is required");
      return;
    }

    if (!selectedCustomer) {
      toast.error("Customer is required");
      return;
    }

    // If IMEI not found, defect note is mandatory
    if (!imeiFound && (!defectNote || defectNote.trim() === "")) {
      toast.error("Defect note is required when IMEI is not found");
      return;
    }

    if (!action) {
      toast.error("Action is required");
      return;
    }

    // Additional validation for "Change Device" action
    if (isChangeDevice) {
      if (!exchangeModel) {
        toast.error("Exchange Model is required");
        return;
      }

      if (!vendorName || vendorName.trim() === "") {
        toast.error("Supplier information is required.");
        return;
      }

      if (!secondLevelAction) {
        toast.error("2nd Level Action is required");
        return;
      }

      if (
        secondLevelAction === "keep_for_vendor" &&
        (!buyingPrice || buyingPrice.trim() === "")
      ) {
        toast.error(
          "Buying Price is required when Keep for Supplier is selected"
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Map action values to API format
      const actionMap = {
        change_device: "Change Device",
        keep_for_repair: "Keep for Repair",
        not_repairable: "Not Repairable",
      };

      // Map second level action values to API format
      const secondLevelActionMap = {
        keep_for_vendor: "Keep for Vendor",
        keep_for_repair: "Keep for Repair",
      };

      // Prepare form data based on API schema
      const formData = {
        imei: imei.trim(),
        customer: selectedCustomer?.id || 0,
        action: actionMap[action] || action,
      };

      // Only include note if it's provided
      if (defectNote && defectNote.trim() !== "") {
        formData.note = defectNote.trim();
      }

      // Only include buying_date if it's provided
      if (buyingDate && buyingDate.trim() !== "") {
        formData.buying_date = convertDateToAPIFormat(buyingDate);
      }

      // Only include product if it's selected
      if (selectedModel?.product?.id || selectedModel?.id) {
        formData.product = selectedModel?.product?.id || selectedModel?.id;
      }

      // Additional fields for Change Device
      if (isChangeDevice) {
        formData.vendor = imeiRecord?.supplier || 0;
        formData.second_level_action =
          secondLevelActionMap[secondLevelAction] || secondLevelAction;
        formData.replacement_product =
          exchangeModel?.product?.id || exchangeModel?.id || 0;

        if (secondLevelAction === "keep_for_vendor" && buyingPrice) {
          formData.buying_price = buyingPrice.trim();
        }
      }

      // Call API directly
      let result;
      if (deviceId) {
        // Update device using the device ID (return_device_id)
        result = await returnVendorAPI.updateDeviceById(deviceId, formData);
        if (result.error) {
          toast.error(result.error.message || "Failed to update device");
          return;
        }

        const successMessage =
          result.response?.message ||
          result.response?.success ||
          "Device updated successfully";
        toast.success(successMessage);
      }

      // Get repair data from API response
      const repairDataResponse = result.response || result.response?.data || {};

      // Prepare repair data for print modal
      const printRepairData = {
        ...repairDataResponse,
        // Ensure all required fields are present
        return_id: repairDataResponse.return_id,
        imei: repairDataResponse.device_imei || repairDataResponse.imei || imei,
        product:
          repairDataResponse.product || selectedModel?.product || selectedModel,
        product_name:
          repairDataResponse.product_name ||
          selectedModel?.name ||
          selectedModel?.product?.name,
        customer:
          repairDataResponse.customer ||
          selectedCustomer?.customer ||
          selectedCustomer,
        customer_name:
          repairDataResponse.customer_name || selectedCustomer?.name,
        technician_note: repairDataResponse.technician_note || defectNote,
        created_at: repairDataResponse.created_at || new Date().toISOString(),
        updated_at:
          repairDataResponse.updated_at ||
          repairDataResponse.created_at ||
          new Date().toISOString(),
        date:
          repairDataResponse.date ||
          repairDataResponse.updated_at ||
          new Date().toISOString(),
      };

      // Set saved repair data and show print modal
      setSavedRepairData(printRepairData);
      setShowPrintModal(true);
    } catch (error) {
      console.error("Error creating label:", error);
      toast.error("Failed to update repair");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Keep component mounted if print modal is open, even if main modal is closed
  if (!isOpen && !showPrintModal) return null;

  return (
    <>
      {/* Main Modal - Only show if isOpen is true and print modal is not open */}
      {isOpen && !showPrintModal && (
        <div
          className="returns-delete-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div className="returns-add-manual-modal">
            <div className="returns-modal-header">
              <div>
                <h3>Edit Supplier Return Transaction</h3>
                <p>
                  {repairId
                    ? "Edit vendor return transaction details below"
                    : "Register returns by filling details below"}
                </p>
              </div>
            </div>

            {loadingRepair && (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">
                    Loading repair data...
                  </span>
                </div>
                <p className="mt-2">Loading repair data...</p>
              </div>
            )}

            {!loadingRepair && (
              <div className="returns-modal-body">
                {/* IMEI Field */}
                <div className="returns-form-group">
                  <label htmlFor="imei-label">
                    IMEI <span className="">*</span>
                  </label>
                  <div className="returns-imei-input-wrapper">
                    <input
                      id="imei"
                      type="text"
                      className={`${isPartsURL ? "returns-imei-input-parts" : "returns-imei-input"} ${
                        imei
                          ? isImeiValid || repairId
                            ? "valid"
                            : "invalid"
                          : ""
                      } ${imeiChecking ? "checking" : ""}`}
                      value={imei}
                      onChange={(e) => setImei(e.target.value)}
                      placeholder="Enter IMEI"
                    />
                    {imei && (
                      <div
                        className={`returns-imei-validation-icon ${
                          isImeiValid || repairId ? "" : "invalid"
                        }`}
                      >
                        {imeiChecking ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                            style={{ width: "15px", height: "15px" }}
                          >
                            <span className="visually-hidden">Checking...</span>
                          </div>
                        ) : isImeiValid || repairId ? (
                          <GreenCheck />
                        ) : (
                          <X size={15} color="#fff" />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Buying Date Field */}
                <div className="returns-form-group">
                  <label htmlFor="imei-label">Buying Date</label>
                  <div className="returns-date-input-wrapper">
                    <img
                      src={CalendarIcon}
                      alt="Calendar"
                      className="returns-date-icon"
                    />
                    <input
                      id="buyingDate"
                      type="text"
                      className={`${isPartsURL ? "returns-date-input-parts" : "returns-date-input"}`}
                      value={buyingDate}
                      onChange={(e) => {
                        const formatted = formatDateInput(e.target.value);
                        setBuyingDate(formatted);
                      }}
                      placeholder="DD/MM/YYYY"
                      maxLength="10"
                    />
                  </div>
                </div>

                {/* Select Model Dropdown */}
                <div className="returns-form-group">
                  <label htmlFor="imei-label">Select Model</label>
                  <div
                    className={`returns-dropdown ${
                      isModelDropdownOpen ? "open" : ""
                    }`}
                    ref={modelDropdownRef}
                  >
                    <button
                      type="button"
                      className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                        selectedModel ? "has-value" : ""
                      }`}
                      onClick={() => setIsModelDropdownOpen((v) => !v)}
                    >
                      <span className={selectedModel ? "has-value" : ""}>
                        {selectedModel
                          ? selectedModel.name.split("\n").map((line, idx) => (
                              <div
                                key={idx}
                                className={
                                  idx === 0
                                    ? "returns-product-name-line1"
                                    : "returns-product-name-line2"
                                }
                              >
                                {line}
                              </div>
                            ))
                          : "Select Model"}
                      </span>
                      <ChevronDown
                        size={16}
                        className="returns-dropdown-chevron"
                        color="#424A53"
                      />
                    </button>

                    {isModelDropdownOpen && (
                      <div className="returns-dropdown-menu">
                        <div className="returns-dropdown-search-container">
                          <div className={`${isPartsURL ? "returns-dropdown-search-box-parts" : "returns-dropdown-search-box"}`}>
                            <Search size={16} color="#8C959F" />
                            <input
                              ref={modelSearchInputRef}
                              type="text"
                              placeholder="Search Model"
                              value={modelSearch}
                              onChange={(e) => setModelSearch(e.target.value)}
                              className="returns-dropdown-search-input"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <ul className="returns-dropdown-list">
                          {loadingModels ? (
                            <li className="returns-dropdown-loading">
                              Loading...
                            </li>
                          ) : filteredModels.length > 0 ? (
                            filteredModels.map((model) => (
                              <li key={model.id}>
                                <button
                                  type="button"
                                  className={
                                    selectedModel?.id === model.id
                                      ? "active"
                                      : ""
                                  }
                                  onClick={() => {
                                    setSelectedModel(model);
                                    setIsModelDropdownOpen(false);
                                    setModelSearch("");
                                  }}
                                >
                                  {model.name.split("\n").map((line, idx) => (
                                    <div
                                      key={idx}
                                      className={
                                        idx === 0
                                          ? "returns-product-name-line1"
                                          : "returns-product-name-line2"
                                      }
                                    >
                                      {line}
                                    </div>
                                  ))}
                                </button>
                              </li>
                            ))
                          ) : (
                            <li className="returns-dropdown-no-results">
                              No models found
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer Dropdown */}
                <div className="returns-form-group">
                  <label htmlFor="imei-label">
                    Customer <span className="">*</span>
                  </label>
                  <div
                    className={`returns-dropdown ${
                      isCustomerDropdownOpen ? "open" : ""
                    }`}
                    ref={customerDropdownRef}
                  >
                    <button
                      type="button"
                      className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                        selectedCustomer ? "has-value" : ""
                      }`}
                      onClick={() => setIsCustomerDropdownOpen((v) => !v)}
                    >
                      <span className={selectedCustomer ? "has-value" : ""}>
                        {selectedCustomer?.name || "Select Customer"}
                      </span>
                      <ChevronDown
                        size={16}
                        className="returns-dropdown-chevron"
                        color="#424A53"
                      />
                    </button>

                    {isCustomerDropdownOpen && (
                      <div className="returns-dropdown-menu">
                        <div className="returns-dropdown-search-container">
                            <div className={`${isPartsURL ? "returns-dropdown-search-box-parts" : "returns-dropdown-search-box"}`}>
                            <Search size={16} color="#8C959F" />
                            <input
                              ref={customerSearchInputRef}
                              type="text"
                              placeholder="Search Customer"
                              value={customerSearch}
                              onChange={(e) =>
                                setCustomerSearch(e.target.value)
                              }
                              className="returns-dropdown-search-input"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <ul className="returns-dropdown-list">
                          {loadingCustomers ? (
                            <li className="returns-dropdown-loading">
                              Loading...
                            </li>
                          ) : filteredCustomers.length > 0 ? (
                            filteredCustomers.map((customer) => (
                              <li key={customer.id}>
                                <button
                                  type="button"
                                  className={
                                    selectedCustomer?.id === customer.id
                                      ? "active"
                                      : ""
                                  }
                                  onClick={() => {
                                    setSelectedCustomer(customer);
                                    setIsCustomerDropdownOpen(false);
                                    setCustomerSearch("");
                                  }}
                                >
                                  {customer.name}
                                </button>
                              </li>
                            ))
                          ) : (
                            <li className="returns-dropdown-no-results">
                              No customers found
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Defect Note */}
                <div className="returns-form-group">
                  <label htmlFor="imei-label">Defect Note</label>
                  <textarea
                    id="defectNote"
                    className={`${isPartsURL ? "returns-textarea-parts" : "returns-textarea"}`}
                    value={defectNote}
                    onChange={(e) => setDefectNote(e.target.value)}
                    placeholder="Enter defect note"
                    rows="3"
                  />
                </div>

                {/* Action Dropdown */}
                <div className="returns-form-group">
                  <label htmlFor="imei-label">
                    {isChangeDevice ? "1st Level Action" : "Action"}{" "}
                    <span className="">*</span>
                  </label>
                  <div
                    className={`returns-dropdown ${
                      isActionDropdownOpen ? "open" : ""
                    }`}
                    ref={actionDropdownRef}
                  >
                    <button
                      type="button"
                      className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                        action ? "has-value" : ""
                      }`}
                      onClick={() => {
                        // Disable dropdown when IMEI is not found
                        if (
                          !repairId &&
                          !imeiFound &&
                          imei &&
                          imei.trim() !== ""
                        ) {
                          return;
                        }
                        setIsActionDropdownOpen((v) => !v);
                      }}
                      disabled={
                        !repairId && !imeiFound && imei && imei.trim() !== ""
                      }
                    >
                      <span className={action ? "has-value" : ""}>
                        {actionOptions.find((opt) => opt.value === action)
                          ?.label || "Select Action"}
                      </span>
                      <ChevronDown
                        size={16}
                        className="returns-dropdown-chevron"
                        color="#424A53"
                      />
                    </button>

                    {isActionDropdownOpen && (
                      <div className="returns-dropdown-menu">
                        <ul className={`${isPartsURL ? "returns-dropdown-list-parts" : "returns-dropdown-list"}`}>
                          {actionOptions.map((option) => (
                            <li key={option.value}>
                              <button
                                type="button"
                                className={
                                  action === option.value ? "active" : ""
                                }
                                onClick={() => {
                                  setAction(option.value);
                                  setIsActionDropdownOpen(false);
                                  // Reset change device specific fields if action changes
                                  if (option.value !== "change_device") {
                                    setExchangeModel(null);
                                    setVendorName("");
                                    setSecondLevelAction("");
                                    setBuyingPrice("");
                                    setExchangeModelSearch("");
                                  }
                                }}
                              >
                                {option.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Conditional fields for "Change Device" action */}
                {isChangeDevice && (
                  <>
                    {/* Select Exchange Model Dropdown */}
                    <div className="returns-form-group">
                      <label htmlFor="exchange-model-label">
                        Select Exchange Model <span className="">*</span>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          className={`returns-dropdown ${
                            isExchangeModelDropdownOpen ? "open" : ""
                          }`}
                          ref={exchangeModelDropdownRef}
                          style={{ flex: 1 }}
                        >
                          <button
                            type="button"
                            className={`${isPartsURL ? "returns-dropdown-trigger-parts" : "returns-dropdown-trigger"} ${
                              exchangeModel ? "has-value" : ""
                            }`}
                            onClick={() =>
                              setIsExchangeModelDropdownOpen((v) => !v)
                            }
                          >
                            <span className={exchangeModel ? "has-value" : ""}>
                              {exchangeModel
                                ? exchangeModel.name
                                    .split("\n")
                                    .map((line, idx) => (
                                      <div
                                        key={idx}
                                        className={
                                          idx === 0
                                            ? "returns-product-name-line1"
                                            : "returns-product-name-line2"
                                        }
                                      >
                                        {line}
                                      </div>
                                    ))
                                : "Choose device model"}
                            </span>
                            <ChevronDown
                              size={16}
                              className="returns-dropdown-chevron"
                              color="#424A53"
                            />
                          </button>

                          {isExchangeModelDropdownOpen && (
                            <div className="returns-dropdown-menu">
                              <div className="returns-dropdown-search-container">
                                <div className={`${isPartsURL ? "returns-dropdown-search-box-parts" : "returns-dropdown-search-box"}`}>
                                  <Search size={16} color="#8C959F" />
                                  <input
                                    ref={exchangeModelSearchInputRef}
                                    type="text"
                                    placeholder="Search Model"
                                    value={exchangeModelSearch}
                                    onChange={(e) =>
                                      setExchangeModelSearch(e.target.value)
                                    }
                                    className="returns-dropdown-search-input"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                              <ul className={`${isPartsURL ? "returns-dropdown-list-parts" : "returns-dropdown-list"}`}>
                                {loadingExchangeModels ? (
                                  <li className="returns-dropdown-loading">
                                    Loading...
                                  </li>
                                ) : filteredExchangeModels.length > 0 ? (
                                  filteredExchangeModels.map((model) => (
                                    <li key={model.id}>
                                      <button
                                        type="button"
                                        className={
                                          exchangeModel?.id === model.id
                                            ? "active"
                                            : ""
                                        }
                                        onClick={() => {
                                          setExchangeModel(model);
                                          setIsExchangeModelDropdownOpen(false);
                                          setExchangeModelSearch("");
                                        }}
                                      >
                                        {model.name
                                          .split("\n")
                                          .map((line, idx) => (
                                            <div
                                              key={idx}
                                              className={
                                                idx === 0
                                                  ? "returns-product-name-line1"
                                                  : "returns-product-name-line2"
                                              }
                                            >
                                              {line}
                                            </div>
                                          ))}
                                      </button>
                                    </li>
                                  ))
                                ) : (
                                  <li className="returns-dropdown-no-results">
                                    No models found
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                        </div>

                        <div style={{ width: "100%", textAlign: "right" }}>
                          {selectedModel &&
                            exchangeModel &&
                            (() => {
                              const modelPrice =
                                selectedModel.product?.latest_selling_price ||
                                0;
                              const exchangePrice =
                                exchangeModel.product?.latest_selling_price ||
                                0;
                              const priceDiff = modelPrice - exchangePrice;
                              const color =
                                priceDiff < 0 ? "#FF383C" : "#0A8900";
                              return (
                                <span
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <span style={{ color: "#424A53" }}>
                                    Price Difference:{" "}
                                  </span>
                                  <span style={{ color }}>
                                    €{priceDiff.toFixed(2)}
                                  </span>
                                </span>
                              );
                            })()}
                        </div>
                      </div>
                    </div>

                    {/* Supplier Display (from IMEI record) */}
                    <div className="returns-form-group">
                      <label htmlFor="vendor-label">
                        Supplier <span className="">*</span>
                      </label>
                      <input
                        id="vendor"
                        type="text"
                        className={`${isPartsURL ? "returns-imei-input-parts" : "returns-imei-input"}`}
                        value={vendorName || ""}
                        readOnly
                        disabled
                        placeholder="Supplier name"
                      />
                    </div>

                    {/* 2nd Level Action Dropdown */}
                    <div className="returns-form-group">
                      <label htmlFor="second-level-action-label">
                        2nd Level Action <span className="">*</span>
                      </label>
                      <div
                        className={`returns-dropdown ${
                          isSecondLevelActionDropdownOpen ? "open" : ""
                        }`}
                        ref={secondLevelActionDropdownRef}
                      >
                        <button
                          type="button"
                          className={`returns-dropdown-trigger ${
                            secondLevelAction ? "has-value" : ""
                          }`}
                          onClick={() =>
                            setIsSecondLevelActionDropdownOpen((v) => !v)
                          }
                        >
                          <span
                            className={secondLevelAction ? "has-value" : ""}
                          >
                            {secondLevelActionOptions.find(
                              (opt) => opt.value === secondLevelAction
                            )?.label || "Select Action"}
                          </span>
                          <ChevronDown
                            size={16}
                            className="returns-dropdown-chevron"
                            color="#424A53"
                          />
                        </button>

                        {isSecondLevelActionDropdownOpen && (
                          <div className="returns-dropdown-menu">
                            <ul className="returns-dropdown-list">
                              {secondLevelActionOptions.map((option) => (
                                <li key={option.value}>
                                  <button
                                    type="button"
                                    className={
                                      secondLevelAction === option.value
                                        ? "active"
                                        : ""
                                    }
                                    onClick={() => {
                                      setSecondLevelAction(option.value);
                                      setIsSecondLevelActionDropdownOpen(false);
                                      // Reset buying price if not "Keep for Supplier"
                                      if (option.value !== "keep_for_vendor") {
                                        setBuyingPrice("");
                                      }
                                    }}
                                  >
                                    {option.label}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Selling Price Input (shown when "Keep for Supplier" is selected) */}
                    {secondLevelAction === "keep_for_vendor" && (
                      <div className="returns-form-group">
                        <label htmlFor="buying-price-label">Selling Price</label>
                        <input
                          id="buyingPrice"
                          type="text"
                          className="returns-imei-input"
                          value={buyingPrice}
                          onChange={(e) => setBuyingPrice(e.target.value)}
                          placeholder="€0.0"
                          readOnly
                          disabled
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="returns-modal-actions">
              <button
                type="button"
                className="returns-cancel-btn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              {/* Hide Create Label button when action is "Change Device" and 2nd Level Action is "Keep for Supplier" */}
              {!(isChangeDevice && secondLevelAction === "keep_for_vendor") && (
                <button
                  type="button"
                  className={`${isPartsURL ? "returns-create-label-btn-parts" : "returns-create-label-btn"}`}
                  onClick={handleCreateLabel}
                  disabled={
                    isSubmitting ||
                    loadingRepair ||
                    !imei ||
                    !selectedCustomer ||
                    !action ||
                    (isChangeDevice &&
                      (!exchangeModel ||
                        !vendorName ||
                        !secondLevelAction ||
                        (secondLevelAction === "keep_for_vendor" &&
                          !buyingPrice)))
                  }
                >
                  <PrintIcon fill={isPartsURL ? "var(--primary-color-parts)" : "var(--primary-color-mobile)"} /> Create Label
                </button>
              )}
              <button
                type="button"
                className={`${isPartsURL ? "returns-save-btn-parts" : "returns-save-btn"}`}
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  loadingRepair ||
                  !imei ||
                  !selectedCustomer ||
                  !action ||
                  (isChangeDevice &&
                    (!exchangeModel ||
                      (!imeiRecord?.supplier && !imeiRecord?.supplier_name) ||
                      !secondLevelAction ||
                      (secondLevelAction === "keep_for_vendor" &&
                        !buyingPrice)))
                }
              >
                {isSubmitting
                  ? "Saving..."
                  : repairId
                  ? "Update Repair"
                  : "Save Return"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Repair Reserve Modal - Render outside main modal overlay */}
      {showPrintModal && savedRepairData && (
        <PrintRepairReserve
          isOpen={showPrintModal}
          onClose={() => {
            setShowPrintModal(false);
            setSavedRepairData(null);

            // Close main modal when print modal closes
            onClose();
          }}
          returnData={savedRepairData}
        />
      )}
    </>
  );
}

export default EditVendorReturnModal;
